/**
 * The header and footer every page shares.
 *
 * Injected rather than copied into each file. There are six pages now and five
 * of them are documents, so a hand-written header per page would mean six
 * places to add a link and six places to forget one. A page opts in by having
 * an element with `data-chrome="header"` or `data-chrome="footer"`.
 *
 * The workbench takes the same bar, above its own shelf row. It used to have a
 * block of its own reading "Signed in / your address / 492 of 500 left / sign
 * out" with no link to the account page at all, so the settings existed and
 * nothing anywhere pointed at them. It then briefly had a dropdown hung off the
 * email address, which hid the whole navigation behind a click on something
 * that reads as a label. One bar, everywhere, is the answer to both.
 */

/**
 * Wren, then the name.
 *
 * This was the name set in Archivo Black with the sprite recipe applied to it by
 * hand: an outline, a lit face, a shade face and a cast shadow. It was a
 * typeface pretending to be a sprite, on a site where everything else genuinely
 * is one, and it was the only thing on the page the tool had not drawn.
 *
 * Now the drawing is a drawing and the type is type. Wren is rendered from his
 * own spec like every other sprite here, and the name is set in Silkscreen,
 * which is the face the headings, buttons and labels already use. The two-step
 * shadow is the panels' own, so the wordmark belongs to the same system as the
 * boxes around it rather than to a rule of its own.
 *
 * The image is a hard-edged 32px sprite, so it is given exact pixel sizes and
 * `image-rendering: pixelated` in base.css. Sizing it in rem would land it on a
 * fraction of a pixel and soften the one thing that must not go soft.
 */
import { signOut } from "./session.js";

const MARK = `
<img class="wordmark-mark" src="/icons/mark.png" alt="" width="32" height="32" />
<span class="wordmark-word">bitwright</span>`;

/**
 * What sits in the middle of the bar.
 *
 * Short on purpose. A nav is a promise about how much there is, and four items
 * on a site with one tool is four items too many.
 */
const LINKS: { label: string; href: string }[] = [
	{ label: "The workbench", href: "/workbench" },
	{ label: "What it costs", href: "/#pricing" },
];

/**
 * A page can name its own links instead.
 *
 * Set `data-links="Label|href, Label|href"` on the header element. The front
 * page wants to point at its own sections rather than at the workbench, and a
 * nav item that sends a first-time visitor into the tool before they know what
 * it is is the wrong first offer. Every other page keeps the shared set.
 */
function linksFor(into: HTMLElement) {
	const own = into.dataset.links;
	if (!own) return LINKS;
	return own
		.split(",")
		.map((pair) => pair.trim().split("|"))
		.filter((p) => p.length === 2)
		.map(([label, href]) => ({ label: label.trim(), href: href.trim() }));
}

/** Everything a footer has to carry, which is mostly the law. */
const LEGAL: { label: string; href: string }[] = [
	// First, and not for legal reasons: it is the only one anybody actually needs,
	// and Stripe prints it on every receipt as the place to go when something is
	// wrong. A support link below four policies is a support link nobody finds.
	{ label: "Support", href: "/support" },
	{ label: "Roadmap", href: "/roadmap" },
	{ label: "Writing", href: "/blog" },
	{ label: "Terms", href: "/terms" },
	{ label: "Privacy", href: "/privacy" },
	{ label: "Cookies", href: "/cookies" },
	{ label: "How AI is used", href: "/ai" },
	{ label: "Acceptable use", href: "/acceptable-use" },
];

/**
 * The company Bitwright is run by. The footer carries the short name as a
 * link; the number and registration belong on the terms and privacy pages.
 */
const MAKER = { label: "Made by Moonlathe Technologies", href: "https://moonlathetechnologies.com/" };

/**
 * Where else Bitwright is.
 *
 * rel="me" on each, which is what lets a profile on the other end point back
 * here and have the two count as the same identity. noopener because every one
 * of these opens in a tab of its own.
 */
const SOCIAL: { label: keyof typeof MARKS; href: string }[] = [
	{ label: "YouTube", href: "https://www.youtube.com/channel/UC8bUAXFTKcdpMv64OmVQqCQ" },
	{ label: "TikTok", href: "https://www.tiktok.com/@bitwright" },
	{ label: "Instagram", href: "https://www.instagram.com/bitwright.sprites/" },
];

/*
 * The three marks, drawn on a 16x16 grid like everything else here.
 *
 * Not the platforms' own SVGs: three anti-aliased brand logos in the footer of
 * a pixel art site look pasted in, and the whole pitch is that we draw things.
 * Recognition lives in the silhouette rather than the curves, so a badge with a
 * play triangle knocked out of it still reads as YouTube at 16 pixels.
 */
const MARKS = {
	YouTube: ["................","................","................",".##############.","################","######.#########","######..########","######...#######","######....######","######...#######","######..########","######.#########","################",".##############.","................","................"],
	TikTok: [".......######...",".......######...",".......########.",".......###..###.",".......###..###.",".......###......",".......###......","...#######......","..########......","..##...###......","..##...###......","..##...##.......","..##...##.......","..#######.......","...#####........","................"],
	Instagram: ["................","..############..",".##############.",".###........###.",".##....##..#.##.",".##..######..##.",".##..##..##..##.",".##.##....##.##.",".##.##....##.##.",".##..##..##..##.",".##..######..##.",".##....##....##.",".###........###.",".##############.","..############..","................"],
} as const;

/**
 * One grid as an <svg>, a run of pixels at a time.
 *
 * Runs are merged rather than emitting a rect per pixel: it is the same picture
 * either way, and this is a tenth of the markup. currentColor rather than a
 * value, so the hover and the two themes are already handled by the link.
 */
const mark = (rows: readonly string[]) => {
	const parts: string[] = [];
	rows.forEach((row, y) => {
		for (let x = 0; x < row.length; ) {
			if (row[x] !== "#") {
				x += 1;
				continue;
			}
			let w = 0;
			while (row[x + w] === "#") w += 1;
			parts.push(`<rect x="${x}" y="${y}" width="${w}" height="1"/>`);
			x += w;
		}
	});
	return `<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false" fill="currentColor" shape-rendering="crispEdges">${parts.join("")}</svg>`;
};

const away = (l: (typeof SOCIAL)[number]) => {
	const a = document.createElement("a");
	a.href = l.href;
	a.rel = "me noopener";
	a.target = "_blank";
	// The picture is decorative and the link needs a name of its own, or it is
	// announced as its own URL.
	a.setAttribute("aria-label", l.label);
	a.title = l.label;
	a.innerHTML = mark(MARKS[l.label]);
	return a;
};

const link = (l: { label: string; href: string }, current: string) => {
	const a = document.createElement("a");
	a.href = l.href;
	a.textContent = l.label;
	// Marked rather than merely styled, so it is announced as well as seen.
	if (l.href === current) a.setAttribute("aria-current", "page");
	return a;
};

/** What /api/me answers with. */
interface Me {
	signedIn: boolean;
	email?: string;
	plan?: string;
	/** The balance. Credits do not expire, so there is no denominator. */
	credits?: number;
	drawn?: number;
}

/**
 * The links, and who is signed in, filled in once the server answers.
 *
 * Account only appears when there is one, because a nav item that takes a
 * stranger to a sign-in page is a nav item that lied about where it went.
 */
function fillNav(
	panel: HTMLElement,
	mine: HTMLElement,
	me: Me,
	path: string,
	set: { label: string; href: string }[],
	known = true,
) {
	const links = document.createElement("nav");
	links.className = "navlinks";
	links.setAttribute("aria-label", "Site");
	links.append(...set.map((l) => link(l, path)));
	if (me.signedIn) links.append(link({ label: "Account", href: "/account" }, path));
	panel.replaceChildren(links);

	/*
	 * Who you are sits in the bar, not in the fold.
	 *
	 * Only the links collapse behind the burger. What is left of your allowance,
	 * and the way in or out, stay visible at every width: the count is the one
	 * number that changes what somebody does next, and finding out you are empty
	 * after opening a menu is finding out too late.
	 */
	mine.replaceChildren();
	/*
	 * Waiting, and saying so.
	 *
	 * The slot already held its size so nothing jumped when the answer arrived,
	 * but an empty gap where a number belongs reads as a page that has finished
	 * loading and has nothing to tell you. A placeholder that is obviously
	 * waiting is the difference between "there is nothing here" and "this is
	 * coming".
	 *
	 * aria-hidden because there is nothing to announce yet: a screen reader
	 * should hear the balance once, when it exists, not hear that it is pending.
	 */
	if (!known) {
		const waiting = document.createElement("span");
		waiting.className = "who-left is-waiting";
		waiting.setAttribute("aria-hidden", "true");
		mine.append(waiting);
		return;
	}

	const chip = document.createElement("span");
	chip.className = "who-left";

	if (me.signedIn) {
		const credits = me.credits ?? 0;

		/*
		 * The balance, and nothing dividing it.
		 *
		 * This briefly carried a gauge, which was right while the allowance was a
		 * monthly cap and became a lie the moment it became a balance: there is no
		 * denominator, so a bar could only ever have read full. A number is the
		 * honest reading, and the colour carries the only judgement worth making,
		 * which is whether you are nearly out.
		 */
		chip.append(
			Object.assign(document.createElement("b"), { textContent: String(credits) }),
			credits === 1 ? " credit" : " credits",
		);
		chip.title = `${me.email ?? "Signed in"} · ${credits} left · ${me.drawn ?? 0} drawn so far`;
		chip.dataset.state = credits <= 5 ? "low" : "";

		const out = document.createElement("button");
		out.type = "button";
		out.className = "btn tiny";
		out.textContent = "Sign out";
		out.addEventListener("click", signOut);
		mine.append(chip, out);
	} else {
		const a = document.createElement("a");
		a.href = "/signin";
		a.className = "go small";
		a.append("Sign in");
		mine.append(a);
	}
}

/**
 * The bar across the top of every page.
 *
 * One row: the mark, the links, what is left, and the way in or out. It
 * replaced a dropdown hung off the visitor's own email address, which put the
 * whole of the site's navigation behind a click on something that reads as a
 * label rather than a control.
 *
 * Below 52rem the links fold behind a burger, because four of them plus a mark
 * plus a button do not fit on a phone and wrapping to a second row costs the
 * workbench a slice of its stage. The allowance stays out of the fold: it is
 * the one number that changes what somebody does next.
 */
export function topNav(into: HTMLElement, path: string) {
	// add, not assign. The workbench's header carries `topbar` for the gutter it
	// has no container to give it, and overwriting className threw that away:
	// the mark ended up hard against the left edge of the screen.
	into.classList.add("topnav");
	into.dataset.open = "0";

	const home = document.createElement("a");
	home.className = "chrome-mark";
	home.href = "/";
	home.setAttribute("aria-label", "Bitwright, back to the front page");
	home.innerHTML = MARK;

	const panel = document.createElement("div");
	panel.className = "navpanel";
	panel.id = "navpanel";

	const mine = document.createElement("div");
	mine.className = "navme";

	const burger = document.createElement("button");
	burger.type = "button";
	burger.className = "burger";
	burger.setAttribute("aria-expanded", "false");
	burger.setAttribute("aria-controls", "navpanel");
	burger.setAttribute("aria-label", "Menu");
	burger.innerHTML = "<span></span><span></span><span></span>";
	burger.addEventListener("click", (e) => {
		e.stopPropagation();
		const open = into.dataset.open !== "1";
		into.dataset.open = open ? "1" : "0";
		burger.setAttribute("aria-expanded", String(open));
	});

	const shut = () => {
		into.dataset.open = "0";
		burger.setAttribute("aria-expanded", "false");
	};
	// Anywhere else, and Escape. A menu opened by accident should not have to be
	// hunted for the way out of.
	document.addEventListener("click", shut);
	document.addEventListener("keydown", (e) => {
		if (e.key === "Escape" && into.dataset.open === "1") {
			shut();
			burger.focus();
		}
	});
	panel.addEventListener("click", (e) => e.stopPropagation());

	into.replaceChildren(home, panel, mine, burger);

	/*
	 * The links go up straight away. Who you are does not, because it is not
	 * known yet.
	 *
	 * This used to render the signed-out bar first and then replace it when
	 * /api/me answered, so every page load flashed "Sign in" and then jumped as
	 * it became a credit count and a Sign out button, which are a different size.
	 * The slot holds its space instead and fills once, and if the request fails
	 * it falls back to signed out, which is the honest thing to show when the
	 * server will not say.
	 */
	const set = linksFor(into);
	fillNav(panel, mine, { signedIn: false }, path, set, false);

	/*
	 * Read once at load, and again whenever something says the balance moved.
	 *
	 * The bar used to be painted exactly once, so a draw took a credit and the
	 * count in the corner went on showing the number it had at page load until
	 * somebody refreshed. The workbench was already re-reading /api/me after
	 * every draw and using it only to decide whether to show one control, so the
	 * answer was being fetched and thrown away while the number nobody could
	 * miss stayed wrong.
	 *
	 * The result is broadcast rather than kept, so the page that needs the plan
	 * can listen instead of asking again. One request, both readers.
	 */
	const read = () =>
		fetch("/api/me")
			.then((r) => r.json() as Promise<Me>)
			.then((me) => {
				fillNav(panel, mine, me, path, set);
				document.dispatchEvent(new CustomEvent<Me>("bitwright:me", { detail: me }));
			})
			// A bar that cannot say who you are is still a bar.
			.catch(() => fillNav(panel, mine, { signedIn: false }, path, set));

	void read();
	// Anything that spends or buys credits says so, and the bar catches up
	// without knowing what happened or who did it.
	document.addEventListener("bitwright:spent", () => void read());
}

function footer(into: HTMLElement, path: string) {
	const nav = document.createElement("nav");
	nav.className = "chrome-legal";
	nav.setAttribute("aria-label", "Policies");
	nav.append(...LEGAL.map((l) => link(l, path)));

	const social = document.createElement("nav");
	social.className = "chrome-social";
	social.setAttribute("aria-label", "Bitwright elsewhere");
	social.append(...SOCIAL.map(away));

	const said = document.createElement("p");
	said.textContent = "Sprites you draw are yours to use, including commercially.";

	// Who is behind it, named on every page rather than only in the terms, and
	// said here once so no page can forget it.
	const maker = document.createElement("a");
	maker.href = MAKER.href;
	maker.rel = "noopener";
	maker.target = "_blank";
	maker.textContent = MAKER.label;

	const who = document.createElement("p");
	who.className = "chrome-who-is";
	who.append(`Bitwright · ${new Date().getFullYear()} · `, maker);

	into.classList.add("chrome-foot");
	into.replaceChildren(nav, social, said, who);
}

const path = location.pathname.replace(/\/$/, "") || "/";
const h = document.querySelector<HTMLElement>('[data-chrome="header"]');
const f = document.querySelector<HTMLElement>('[data-chrome="footer"]');
if (h) topNav(h, path);
if (f) footer(f, path);
